import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../context/TranslationContext';
import { Button } from '../';
import { get } from '../../utils/api-router';
import debug from '@commons-shared/debug';

/**
 * Composant de sélection dynamique qui récupère les valeurs depuis la base
 * @param {Object} props
 * @param {string} props.fieldName - Nom du champ
 * @param {string} props.value - Valeur actuelle
 * @param {Function} props.onChange - Fonction de changement
 * @param {boolean} props.required - Champ requis
 * @param {string} props.placeholder - Texte placeholder
 * @param {string} props.apiEndpoint - Endpoint API pour récupérer les données
 * @param {string} props.dynamicSource - Nom du champ source pour les valeurs uniques
 * @param {Array} props.fallbackOptions - Options de fallback si pas de données
 */
export default function DynamicSelectField({
  fieldName,
  value,
  onChange,
  required,
  placeholder,
  apiEndpoint,
  dynamicSource,
  fallbackOptions = []
}) {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [loading, setLoading] = useState(false);

  // Charger les options dynamiques
  useEffect(() => {
    loadDynamicOptions();
  }, [apiEndpoint, dynamicSource]);

  const loadDynamicOptions = async () => {
    if (!apiEndpoint || !dynamicSource) {
      debug.log('DynamicSelectField', 'apiEndpoint ou dynamicSource manquant, utilisation des options de fallback');
      setOptions(fallbackOptions);
      return;
    }

    try {
      setLoading(true);
      debug.log('DynamicSelectField', `Chargement des options pour ${dynamicSource} depuis ${apiEndpoint}`);
      
      // Récupérer toutes les données et extraire les valeurs uniques
      const response = await get(`${apiEndpoint}?limit=1000&format=json`);
      
      debug.log('DynamicSelectField', 'Réponse API reçue:', response);
      
      if (response.success) {
        // Essayer d'abord response.data, puis les clés spécifiques
        let dataArray = response.data;
        
        if (!Array.isArray(dataArray)) {
          // Chercher dans toutes les propriétés de la réponse pour trouver un array
          const responseKeys = Object.keys(response);
          debug.log('DynamicSelectField', 'Clés disponibles dans la réponse:', responseKeys);
          
          for (const key of responseKeys) {
            if (Array.isArray(response[key])) {
              dataArray = response[key];
              debug.log('DynamicSelectField', `Données trouvées dans response.${key} (${dataArray.length} éléments):`, dataArray.slice(0, 2));
              break;
            }
          }
        }
        
        // Si le tableau est vide, utiliser les fallbackOptions
        if (Array.isArray(dataArray) && dataArray.length === 0) {
          debug.warn('DynamicSelectField', `Aucune donnée disponible pour ${dynamicSource}, utilisation des fallbackOptions`);
          setOptions(fallbackOptions);
          return;
        }
        
        if (Array.isArray(dataArray) && dataArray.length > 0) {
          // Vérifier si le champ dynamicSource existe dans les données
          const firstItem = dataArray[0];
          const availableFields = Object.keys(firstItem);
          debug.log('DynamicSelectField', `Champs disponibles dans les données:`, availableFields);
          debug.log('DynamicSelectField', `Recherche du champ: ${dynamicSource}`);
          
          if (!availableFields.includes(dynamicSource)) {
            debug.warn('DynamicSelectField', `Le champ ${dynamicSource} n'existe pas dans les données. Champs disponibles: ${availableFields.join(', ')}`);
            setOptions(fallbackOptions);
            return;
          }
          
          const uniqueValues = [...new Set(
            dataArray
              .map(item => item[dynamicSource])
              .filter(val => val !== null && val !== undefined && val !== '' && String(val).trim() !== '')
          )].sort();

          debug.log('DynamicSelectField', `Valeurs uniques trouvées pour ${dynamicSource}:`, uniqueValues);

          const dynamicOptions = uniqueValues.map(val => ({
            value: String(val).trim(),
            label: String(val).trim()
          }));

          // Combiner avec les options de fallback si elles existent
          const allOptions = [...dynamicOptions];
          fallbackOptions.forEach(fallback => {
            if (!allOptions.find(opt => opt.value === fallback.value)) {
              allOptions.push(fallback);
            }
          });

          setOptions(allOptions);
          debug.log('DynamicSelectField', `${allOptions.length} options finales chargées pour ${dynamicSource}:`, allOptions.map(opt => opt.value));
        } else {
          debug.warn('DynamicSelectField', 'Aucun tableau de données trouvé dans la réponse, utilisation des options de fallback');
          setOptions(fallbackOptions);
        }
      } else {
        debug.warn('DynamicSelectField', 'Réponse invalide, utilisation des options de fallback');
        setOptions(fallbackOptions);
      }
    } catch (error) {
      debug.error('DynamicSelectField', `Erreur lors du chargement des options: ${error.message}`);
      setOptions(fallbackOptions);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === '__custom__') {
      setIsCustomMode(true);
      setCustomValue('');
    } else {
      onChange({
        target: {
          name: fieldName,
          value: selectedValue
        }
      });
    }
  };

  const handleCustomSubmit = () => {
    if (customValue.trim()) {
      onChange({
        target: {
          name: fieldName,
          value: customValue.trim()
        }
      });
      
      // Ajouter la nouvelle valeur aux options
      const newOption = { value: customValue.trim(), label: customValue.trim() };
      if (!options.find(opt => opt.value === newOption.value)) {
        setOptions(prev => [...prev, newOption].sort((a, b) => a.label.localeCompare(b.label)));
      }
      
      setIsCustomMode(false);
      setCustomValue('');
    }
  };

  const handleCustomCancel = () => {
    setIsCustomMode(false);
    setCustomValue('');
  };

  const handleCustomKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCustomSubmit();
    } else if (e.key === 'Escape') {
      handleCustomCancel();
    }
  };

  if (isCustomMode) {
    return (
      <div className="wa-dynamic-select-custom">
        <div className="wa-custom-input-group">
          <input
            type="text"
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            onKeyDown={handleCustomKeyDown}
            placeholder={placeholder}
            autoFocus
            className="wa-custom-input"
          />
          <div className="wa-custom-buttons">
            <Button
              type="button"
              variant="success"
              size="sm"
              onClick={handleCustomSubmit}
              disabled={!customValue.trim()}
            >
              ✓
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleCustomCancel}
            >
              ✗
            </Button>
          </div>
        </div>
        <small className="wa-help-text">
          {t('ADMIN', 'CUSTOM_VALUE_HELP', 'Appuyez sur Entrée pour valider ou Échap pour annuler')}
        </small>
      </div>
    );
  }

  return (
    <div className="wa-dynamic-select">
      <select
        id={fieldName}
        name={fieldName}
        value={value || ''}
        onChange={handleSelectChange}
        required={required}
        disabled={loading}
        className="wa-dynamic-select-field"
      >
        <option value="">
          {loading 
            ? t('ADMIN', 'LOADING_OPTIONS', 'Chargement...') 
            : t('ADMIN', 'SELECT_OPTION', 'Sélectionner...')
          }
        </option>
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
        <option value="__custom__" className="wa-custom-option">
          {t('ADMIN', 'ADD_CUSTOM_VALUE', '+ Nouvelle valeur...')}
        </option>
      </select>
      
      {loading && (
        <small className="wa-loading-text">
          {t('ADMIN', 'LOADING_DYNAMIC_OPTIONS', 'Chargement des options...')}
        </small>
      )}
    </div>
  );
}
